import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PackagePlus, ArrowRightLeft } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";

const kgFmt = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} Kg`;
const empty = { tanggal: todayISO(), nama: "", kg: "", nilai: 0, supplier: "", keterangan: "" };

export const StokBahanCards = ({ items, testid = "stok-bahan-cards" }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={testid}>
    {items.length === 0 && <div className="col-span-2 lg:col-span-4 card-flat p-4 text-sm text-slate-400">Belum ada stok bahan. Catat Bahan Masuk atau terima penjualan internal dari pabrik lain.</div>}
    {items.map((b) => (
      <div key={b.nama} className="card-flat p-3 md:p-4" data-testid={`stok-bahan-${b.nama}`}>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 truncate">{b.nama}</div>
        <div className={`font-mono text-lg md:text-xl mt-1 ${b.sisa_kg > 0 ? "text-slate-900" : "text-slate-400"}`}>{kgFmt(b.sisa_kg)}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">Rata-rata {rupiah(b.harga_rata)}/Kg</div>
      </div>
    ))}
  </div>
);

export default function BahanBriket() {
  const [items, setItems] = useState([]);
  const [stok, setStok] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/bahan-briket"), api.get("/stok-bahan-briket")]);
      setItems(a.data); setStok(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });
  const openNew = () => { setEditId(null); setForm({ ...empty, tanggal: todayISO() }); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it, tanggal: toDateInput(it.tanggal) }); setOpen(true); };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.tanggal || !form.nama.trim()) { toast.error("Tanggal & nama bahan wajib diisi"); return; }
    if (!(Number(form.kg) > 0)) { toast.error("Jumlah (Kg) wajib lebih dari 0"); return; }
    const payload = { tanggal: form.tanggal, nama: form.nama.trim(), kg: Number(form.kg), nilai: Number(form.nilai || 0), supplier: form.supplier || "", keterangan: form.keterangan || "" };
    try {
      if (editId) await api.put(`/bahan-briket/${editId}`, payload); else await api.post("/bahan-briket", payload);
      toast.success("Bahan masuk tersimpan"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (it) => {
    if (!window.confirm("Hapus bahan masuk ini? Pengeluaran terkait ikut terhapus.")) return;
    try { await api.delete(`/bahan-briket/${it.id}`); toast.success("Terhapus"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(items, 10);

  return (<>
    <PageHeader title="Bahan Masuk Briket" subtitle="Stok bahan baku briket: otomatis dari penjualan internal pabrik lain (kulitan, arang pendam, batok) atau beli dari luar."
      actions={<Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-bahan-btn"><Plus size={14} className="mr-1"/>Bahan Masuk</Button>}/>
    <div className="p-4 md:p-8 space-y-5">
      <StokBahanCards items={stok}/>
      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm min-w-[640px]" data-testid="bahan-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3">Tanggal</th><th className="p-3">Bahan</th><th className="p-3">Dari</th><th className="p-3 text-right">Jumlah</th>
              <th className="p-3 text-right">Nilai</th><th className="p-3 text-right">Harga/Kg</th><th className="p-3 text-right">Aksi</th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Belum ada bahan masuk.</td></tr>}
              {paged.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`bahan-row-${it.id}`}>
                  <td className="p-3 whitespace-nowrap">{tanggalID(it.tanggal)}</td>
                  <td className="p-3 font-medium text-slate-800">{it.nama}</td>
                  <td className="p-3">
                    {it.internal
                      ? <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-200 whitespace-nowrap" data-testid={`bahan-internal-${it.id}`}><ArrowRightLeft size={11}/>{it.supplier}{it.transfer_gratis ? " · transfer" : ""}</span>
                      : (it.supplier || "-")}
                  </td>
                  <td className="p-3 text-right font-mono font-semibold">{kgFmt(it.kg)}</td>
                  <td className="p-3 text-right font-mono">{rupiah(it.nilai)}</td>
                  <td className="p-3 text-right font-mono brand-text">{rupiah(it.kg > 0 ? it.nilai / it.kg : 0)}</td>
                  <td className="p-3">
                    {it.internal ? <div className="text-right text-[11px] text-slate-400">Dari pabrik penjual</div> : (
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(it)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-bahan-${it.id}`}><Pencil size={14}/></button>
                        <button onClick={() => del(it)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-bahan-${it.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                      </div>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="bahan-pagination"/>
      </div>
    </div>

    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bahan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PackagePlus size={18} className="text-violet-600"/>{editId ? "Ubah" : "Catat"} Bahan Masuk</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} data-testid="bahan-tanggal"/></div>
          <div className="space-y-1.5"><Label>Nama Bahan <span className="text-rose-500">*</span></Label>
            <Input list="bahan-list" value={form.nama} onChange={upd("nama")} placeholder="mis. Arang Kulitan/Remuk" data-testid="bahan-nama"/>
            <datalist id="bahan-list">{stok.map((b) => <option key={b.nama} value={b.nama}/>)}</datalist>
          </div>
          <div className="space-y-1.5"><Label>Jumlah (Kg) <span className="text-rose-500">*</span></Label><Input type="number" min="0" step="0.01" value={form.kg} onChange={upd("kg")} data-testid="bahan-kg"/></div>
          <div className="space-y-1.5"><Label>Total Nilai Beli (Rp)</Label><CurrencyInput value={form.nilai} onValueChange={upd("nilai")} data-testid="bahan-nilai"/></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Supplier</Label><Input value={form.supplier} onChange={upd("supplier")} data-testid="bahan-supplier"/></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} data-testid="bahan-keterangan"/></div>
          <button type="submit" className="hidden" aria-hidden="true"/>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="bahan-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
